import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";
import { ArrowLeft, ArrowLeftSharp } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { BsArrowLeft } from "react-icons/bs";

export default function SchemeDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { schemeId } = location.state || {};
  const [scheme, setScheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchSchemeDetails();
  }, [schemeId]);

  const fetchSchemeDetails = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/scheme/getById`, {
        params: { schemeId: schemeId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setScheme(data.data);
    } catch (error) {
      console.error("Error fetching scheme details:", error);
      toast.error("Failed to fetch scheme details");
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Not set";
    return new Date(timestamp).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      Completed: "bg-green-600 text-white",
      Ongoing: "bg-blue-600 text-white",
      Planning: "bg-yellow-700 text-white",
      Approved: "bg-blue-600 text-white",
      Started: "bg-blue-400 text-white",
      "On Hold": "bg-orange-700 text-white",
      Cancelled: "bg-red-700 text-white",
      "Partially Completed": "bg-teal-700 text-white",
    };
    return colors[status] || "bg-gray-700 text-white";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      Completed: "bg-green-600 text-white",
      Partial: "bg-yellow-700 text-white",
      Pending: "bg-orange-700 text-white",
      Hold: "bg-red-700 text-white",
      Rejected: "bg-gray-700 text-white",
    };
    return colors[status] || "bg-gray-500 text-white";
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return "bg-green-500";
    if (percentage >= 75) return "bg-blue-500";
    if (percentage >= 50) return "bg-yellow-500";
    if (percentage >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  // Tab configuration with icons and colors
  const tabs = [
    {
      id: "overview",
      label: "Overview",
      icon: "📊",
      gradient: "from-blue-500 to-blue-600",
      hoverGradient: "from-blue-600 to-blue-700",
      description: "View scheme details",
    },
    {
      id: "financial",
      label: "Financial",
      icon: "💰",
      gradient: "from-green-500 to-green-600",
      hoverGradient: "from-green-600 to-green-700",
      description: "Manage budgets & expenses",
    },
    {
      id: "approvals",
      label: "Approvals",
      icon: "✅",
      gradient: "from-purple-500 to-purple-600",
      hoverGradient: "from-purple-600 to-purple-700",
      description: "Track approval status",
    },
    {
      id: "execution",
      label: "Execution",
      icon: "⚡",
      gradient: "from-orange-500 to-orange-600",
      hoverGradient: "from-orange-600 to-orange-700",
      description: "Monitor progress",
    },
    {
      id: "documents",
      label: "Documents",
      icon: "📑",
      gradient: "from-gray-600 to-gray-700",
      hoverGradient: "from-gray-700 to-gray-800",
      description: "Access files & reports",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Loading scheme details...
          </p>
        </div>
      </div>
    );
  }

  if (!scheme) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 text-gray-400">❌</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Scheme not found
          </h3>
          <p className="text-gray-600 mb-6">
            The requested scheme could not be found.
          </p>
          <button
            onClick={() => navigate("/schemes")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Back to Schemes
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = Math.max(
    0,
    Math.min(100, scheme.progressPercentage || 0)
  );

  return (
    <div className="min-h-screen -mt-4 -mx-4">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <IconButton onClick={() => navigate(-1)}>
                <BsArrowLeft className="text-gray-700" />
              </IconButton>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {scheme.schemeName}
                </h1>
                <p className="text-gray-600 mt-1 text-sm">
                  {scheme.district} • {scheme.block} • {scheme.panchayat}
                </p>
              </div>
            </div>

            <div className="flex space-x-2 justify-end">
              <button className="bg-blue-600 text-white rounded-lg px-3 py-1">Progress Update</button>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold ${getStatusColor(
                  scheme.schemeStatus
                )}`}
              >
                Scheme Status - {scheme.schemeStatus}
              </span>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-semibold ${getPaymentStatusColor(
                  scheme.paymentStatus
                )}`}
              >
                Payment Status - {scheme.paymentStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title="Total Allocation"
            value={formatAmount(scheme.allocatedAmount)}
            icon="💰"
            color="blue"
          />
          <MetricCard
            title="Amount Spent"
            value={formatAmount(scheme.totalSpent)}
            icon="💸"
            color="green"
          />
          <MetricCard
            title="Amount Remaining"
            value={formatAmount(scheme.amountRemaining)}
            icon="📊"
            color="blue"
          />
          <MetricCard
            title="Progress"
            value={`${progressPercentage.toFixed(1)}%`}
            icon="📈"
            color="orange"
          />
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-800">
              Project Progress
            </span>
            <span className="text-sm font-bold text-gray-900">
              {progressPercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className={`h-2 rounded-full ${getProgressColor(
                progressPercentage
              )}`}
            />
          </div>
        </div>

        {/* Enhanced Cards Navigation */}
        <div className="flex flex-row gap-3 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              className={`bg-gradient-to-br ${tab.gradient
                } rounded-lg p-3 text-white shadow-md hover:shadow-lg transition-all duration-200 text-left border-2 min-w-[180px] flex items-center gap-3 ${activeTab === tab.id
                  ? "border-white ring-1 ring-white ring-opacity-50"
                  : "border-transparent"
                }`}
            >
              <div className="w-10 h-10 rounded-md bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{tab.icon}</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm text-white truncate">
                  {tab.label}
                </h3>
                <p className="text-white font-normal text-opacity-80 text-xs truncate">
                  {tab.description}
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Basic Information">
                  <InfoField
                    label="Financial Year"
                    value={scheme.financialYear}
                  />
                  <InfoField label="Sector" value={scheme.sector} />
                  <InfoField label="Sub Sector" value={scheme.subSector} />

                </SectionCard>

                <SectionCard title="Location Details">
                  <InfoField label="District" value={scheme.district} />
                  <InfoField label="Block" value={scheme.block} />
                  <InfoField
                    label="Gram Panchayat"
                    value={scheme.gramPanchayat || scheme.panchayat}
                  />
                  <InfoField label="Land Address" value={scheme.landAddress} />
                  {scheme.coordinates && (
                    <InfoField
                      label="Coordinates"
                      value={`${scheme.coordinates.latitude}, ${scheme.coordinates.longitude}`}
                    />
                  )}
                </SectionCard>

                <SectionCard title="Project Details">
                  <InfoField
                    label="Utility Purpose"
                    value={scheme.utilityPurpose}
                  />
                  <InfoField
                    label="Impact Description"
                    value={scheme.impactDescription}
                  />
                  <InfoField label="Land Nature" value={scheme.landNature} />
                  <InfoField
                    label="Land Suitability"
                    value={scheme.landSuitability}
                  />
                </SectionCard>

                <SectionCard title="Officials">
                  <InfoField label="DDC-cum-CEO" value={scheme.ddcCumCEOName} />
                  <InfoField
                    label="District Engineer"
                    value={scheme.districtEngineerName}
                  />
                  <InfoField label="DDO" value={scheme.ddo} />
                  <InfoField label="DDO Code" value={scheme.ddoCode} />
                </SectionCard>
              </motion.div>
            )}

            {/* Financial Tab */}
            {activeTab === "financial" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Financial Details">
                  <InfoField
                    label="Total Allocated"
                    value={formatAmount(scheme.totalAllocated)}
                  />
                  <InfoField
                    label="Allocated Amount"
                    value={formatAmount(scheme.allocatedAmount)}
                  />
                  <InfoField
                    label="Allocation Letter No"
                    value={scheme.allocationLetterNo}
                  />
                  <InfoField
                    label="Technical Sanction Amount"
                    value={formatAmount(scheme.technicalSanctionAmount)}
                  />
                  <InfoField
                    label="Administrative Approval Amount"
                    value={formatAmount(scheme.administrativeApprovalAmount)}
                  />
                  <InfoField
                    label="Amount Remaining"
                    value={formatAmount(scheme.amountRemaining)}
                  />
                  <InfoField
                    label="UC Amount"
                    value={formatAmount(scheme.ucAmount)}
                  />
                </SectionCard>


              </motion.div>
            )}

            {/* Approvals Tab */}
            {activeTab === "approvals" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Approval Details">
                  <InfoField
                    label="Need Assessment"
                    value={scheme.needAssessment}
                  />
                  <InfoField
                    label="Feasibility Study"
                    value={scheme.feasibilityStudy}
                  />
                  <InfoField label="DPR" value={scheme.dpr} />
                  <InfoField
                    label="Revenue Generation"
                    value={scheme.revenueGeneration ? "Yes" : "No"}
                  />
                  <InfoField
                    label="Board Approval"
                    value={scheme.boardApproval ? "Yes" : "No"}
                  />
                </SectionCard>

                <SectionCard title="Sanction Details">
                  <InfoField
                    label="Technical Sanction Authority"
                    value={scheme.technicalSanctionAuthority}
                  />
                  <InfoField
                    label="Administrative Approval Authority"
                    value={scheme.administrativeApprovalAuthority}
                  />
                  <InfoField
                    label="Admin Approval Letter No"
                    value={scheme.administrativeApprovalLetterNo}
                  />
                  <InfoField
                    label="Admin Approval Date"
                    value={formatDate(scheme.administrativeApprovalDate)}
                  />
                </SectionCard>
              </motion.div>
            )}

            {/* Execution Tab */}
            {activeTab === "execution" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                <SectionCard title="Execution Timeline">
                  <InfoField
                    label="Scheme Start Date"
                    value={formatDate(scheme.schemeStartDate)}
                  />
                  <InfoField
                    label="Completion Date"
                    value={formatDate(scheme.completionDate)}
                  />
                  <InfoField
                    label="Work Order Date"
                    value={formatDate(scheme.workOrderDate)}
                  />
                  <InfoField
                    label="Tender Status"
                    value={scheme.tenderStatus}
                  />
                  <InfoField label="Agency Name" value={scheme.agencyName} />
                </SectionCard>

                <SectionCard title="Monitoring">

                  <InfoField
                    label="UC Sent Letter No"
                    value={scheme.ucSentLetterNo}
                  />
                  <InfoField
                    label="UC Sent Date"
                    value={formatDate(scheme.ucSentDate)}
                  />
                  <InfoField
                    label="Last Field Visit Date"
                    value={formatDate(scheme.lastFieldVisitDate)}
                  />
                  <InfoField
                    label="Field Visit Officials"
                    value={scheme.fieldVisitOfficials}
                  />
                </SectionCard>
              </motion.div>
            )}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                <SectionCard title="Uploaded Documents">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {scheme.geoReportDoc && (
                      <a href={scheme.geoReportDoc} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Geo Report</span>
                      </a>
                    )}
                    {scheme.feasibilityStudyFile && (
                      <a href={scheme.feasibilityStudyFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Feasibility Study</span>
                      </a>
                    )}
                    {scheme.dprFile && (
                      <a href={scheme.dprFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">DPR File</span>
                      </a>
                    )}
                    {scheme.administrativeApprovalFile && (
                      <a href={scheme.administrativeApprovalFile} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Admin Approval File</span>
                      </a>
                    )}

                    {scheme.tenderNoticeAttachment && typeof scheme.tenderNoticeAttachment === "string" && scheme.tenderNoticeAttachment.startsWith("http") && (
                      <a href={scheme.tenderNoticeAttachment} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-blue-50 p-3 rounded-lg border border-blue-100 transition-colors">
                        <span className="text-xl">📑</span>
                        <span className="font-medium text-sm">Tender Notice Attachment</span>
                      </a>
                    )}
                    {!scheme.geoReportDoc && !scheme.feasibilityStudyFile && !scheme.dprFile && !scheme.administrativeApprovalFile && (!scheme.tenderNoticeAttachment || typeof scheme.tenderNoticeAttachment !== "string" || !scheme.tenderNoticeAttachment.startsWith("http")) && (
                      <p className="text-gray-500 text-sm py-2">No documents available</p>
                    )}
                  </div>
                </SectionCard>

                <SectionCard title="Field Visit Notes">
                  {scheme.fieldVisitNotes &&
                    scheme.fieldVisitNotes.length > 0 ? (
                    <div className="space-y-3">
                      {scheme.fieldVisitNotes.map((note, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-3 rounded border border-gray-200"
                        >
                          <p className="text-gray-700 text-sm">{note}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm py-2">
                      No field visit notes available
                    </p>
                  )}
                </SectionCard>

                <SectionCard title="Remarks">
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-700">
                      {scheme.remarks || "No remarks available"}
                    </p>
                  </div>
                </SectionCard>

                <SectionCard title="CO Report">
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-700">
                      {scheme.coReport || "No CO report available"}
                    </p>
                  </div>
                </SectionCard>
              </motion.div>
            )}
          </div>
        </div>
        {/* <div className="mt-12 flex items-center justify-end">
          <button className="bg-blue-700 text-white">Accept Request</button>
        </div> */}
      </div>
    </div>
  );
}

// Enhanced Section Card Component
const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 hover:border-gray-300 transition-colors">
    <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
      {title}
    </h3>
    <div className="space-y-3">{children}</div>
  </div>
);

// Metric Card Component
const MetricCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    orange: "bg-orange-500",
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 hover:border-gray-300 transition-colors">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]} text-white`}>
          <span className="text-lg">{icon}</span>
        </div>
      </div>
    </div>
  );
};

// Info Field Component
const InfoField = ({ label, value }) => (
  <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
    <span className="text-sm font-medium text-gray-600 flex-shrink-0 pr-4">
      {label}
    </span>
    <span className="text-sm text-gray-900 font-semibold text-right flex-1">
      {value || "Not available"}
    </span>
  </div>
);
